import { useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useQuery } from '@tanstack/react-query'
import { useLang, type Key as TKey } from '../i18n'
import { api } from '../lib/api'
import type { TemplateCatalogue, TemplateCategory, TemplateEntry } from '../lib/template-catalogue'
import type { ArtefactType } from '../lib/artefact-type'
import { TEMPLATE_CATEGORY_MAX_DEPTH } from '../lib/template-library'

const TYPE_LABEL: Record<ArtefactType, TKey> = {
  prose: 'artefact_type_prose',
  slides: 'artefact_type_slides',
  grid: 'artefact_type_grid',
}

/** The inline sentence for a refused publish — the backend's codes (routes/artefact-templates.ts). */
function refusalKey(e: unknown): TKey {
  const { status, code } = (e ?? {}) as { status?: number; code?: string }
  if (status === 403) return 'pt_err_forbidden'
  if (status === 409) return 'pt_err_busy'
  if (code === 'same_artefact') return 'pt_err_same'
  if (status === 422) return 'pt_err_type'
  if (status === 404) return 'pt_err_not_found'
  if (code === 'bad_category') return 'pt_err_category'
  return 'prompt_failed'
}

/** 1 for a top-level category, 2 for a sub-category. */
function depthOf(categories: TemplateCategory[], id: string): number {
  const c = categories.find((x) => x.id === id)
  return c?.parentId ? 2 : 1
}

/** The folder titles from the root down to `id` — what the publish API takes as `category`. */
function pathOf(categories: TemplateCategory[], id: string | null): string[] {
  const out: string[] = []
  let current = id ? categories.find((c) => c.id === id) : undefined
  while (current && out.length < TEMPLATE_CATEGORY_MAX_DEPTH) {
    out.unshift(current.title)
    const parentId: string | null = current.parentId
    current = parentId ? categories.find((c) => c.id === parentId) : undefined
  }
  return out
}

/**
 * "Publish as template…" (artefact templates 6/8) — copies an artefact of a bid into the hidden
 * template library, as a NEW template or REPLACING an existing one of the same type, at the library
 * root or in a category (created on the fly, 2 levels at most). Tenant admins only: LeftNav offers
 * it to them alone, and the backend checks again (403).
 *
 * A form of its own rather than PromptModal: it has a mode, a template picker, three fields and a
 * category choice. Focus is trapped inside while it is open and returns to the opener on close.
 */
export function PublishTemplateDialog({
  bidId,
  artefactId,
  artefactTitle,
  artefactType,
  onPublished,
  onClose,
}: {
  bidId: string
  artefactId: string
  artefactTitle: string
  artefactType: ArtefactType
  onPublished: (version: number) => void
  onClose: () => void
}) {
  const { t } = useLang()
  const ids = useId()
  const catalogue = useQuery({ queryKey: ['artefact-templates'], queryFn: () => api.artefactTemplates() })
  const categories = catalogue.data?.categories ?? []

  const [mode, setMode] = useState<'new' | 'replace'>('new')
  const [replaces, setReplaces] = useState<string | null>(null)
  const [name, setName] = useState(artefactTitle)
  const [description, setDescription] = useState('')
  /** '' = the library root, else a category id. */
  const [categoryId, setCategoryId] = useState('')
  const [creatingCategory, setCreatingCategory] = useState(false)
  const [newCategory, setNewCategory] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const busyRef = useRef(false)
  const dialogRef = useRef<HTMLDivElement | null>(null)
  const firstRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null
    firstRef.current?.focus()
    return () => prev?.focus()
  }, [])

  // A sub-category cannot hold another level: no 3rd-level creation is offered.
  const canCreateHere = categoryId === '' || depthOf(categories, categoryId) < TEMPLATE_CATEGORY_MAX_DEPTH
  const creating = creatingCategory && canCreateHere

  function chooseMode(next: 'new' | 'replace'): void {
    setMode(next)
    setError(null)
    if (next === 'new') {
      setReplaces(null)
      setName(artefactTitle)
      setDescription('')
      setCategoryId('')
    }
  }
  function pickTemplate(tpl: TemplateEntry): void {
    setReplaces(tpl.id)
    setName(tpl.name)
    setDescription(tpl.description)
    setCategoryId(tpl.categoryId ?? '')
    setCreatingCategory(false)
    setError(null)
  }

  const ready =
    name.trim() !== '' && (mode === 'new' || replaces !== null) && (!creating || newCategory.trim() !== '')

  const submit = (): void => {
    if (busyRef.current || !ready) return
    const category = pathOf(categories, categoryId || null)
    if (creating) category.push(newCategory.trim())
    busyRef.current = true
    setBusy(true)
    setError(null)
    api
      .publishArtefactTemplate({
        bidId,
        artefactId,
        name: name.trim(),
        description: description.trim(),
        category,
        ...(mode === 'replace' && replaces ? { replaces } : {}),
      })
      .then(
        (res) => onPublished(res.version),
        (e: unknown) => {
          busyRef.current = false
          setBusy(false)
          setError(t(refusalKey(e)))
        },
      )
  }
  const close = (): void => {
    if (!busyRef.current) onClose()
  }

  /** Keep Tab / Shift+Tab inside the dialog. */
  function trapFocus(e: React.KeyboardEvent): void {
    if (e.key === 'Escape') {
      close()
      return
    }
    if (e.key !== 'Tab' || !dialogRef.current) return
    const focusable = [
      ...dialogRef.current.querySelectorAll<HTMLElement>('button, input, select, textarea, [tabindex]:not([tabindex="-1"])'),
    ].filter((el) => !(el as HTMLButtonElement).disabled)
    if (focusable.length === 0) return
    const first = focusable[0]!
    const last = focusable[focusable.length - 1]!
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault()
      first.focus()
    }
  }

  const categoryLabel = (c: TemplateCategory): string =>
    c.parentId ? pathOf(categories, c.id).join(' / ') : c.title

  return createPortal(
    <div className="modal-backdrop" onMouseDown={close}>
      <div
        ref={dialogRef}
        className="modal publish-tpl-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${ids}-title`}
        aria-busy={busy || undefined}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={trapFocus}
      >
        <div className="modal-head">
          <h2 id={`${ids}-title`}>{t('pt_title')}</h2>
          <button className="modal-close" aria-label={t('close')} onClick={close} disabled={busy}>✕</button>
        </div>
        <form
          className="modal-body prompt-body publish-tpl-form"
          onSubmit={(e) => {
            e.preventDefault()
            submit()
          }}
        >
          <fieldset className="prompt-choice" disabled={busy}>
            <legend>{t('pt_mode')}</legend>
            <label>
              <input
                ref={firstRef}
                type="radio"
                name={`${ids}-mode`}
                checked={mode === 'new'}
                onChange={() => chooseMode('new')}
              />
              {t('pt_mode_new')}
            </label>
            <label>
              <input type="radio" name={`${ids}-mode`} checked={mode === 'replace'} onChange={() => chooseMode('replace')} />
              {t('pt_mode_replace')}
            </label>
          </fieldset>

          {mode === 'replace' && (
            <TemplatePicker
              name={`${ids}-tpl`}
              catalogue={catalogue.data}
              loading={catalogue.isLoading}
              type={artefactType}
              selected={replaces}
              disabled={busy}
              onPick={pickTemplate}
              categoryLabel={categoryLabel}
            />
          )}

          <label className="prompt-label">
            {t('pt_name')}
            <input className="prompt-input" value={name} disabled={busy} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="prompt-label">
            {t('pt_description')}
            <textarea
              className="prompt-input prompt-textarea"
              rows={4}
              value={description}
              disabled={busy}
              onChange={(e) => setDescription(e.target.value)}
            />
          </label>

          <label className="prompt-label">
            {t('pt_category')}
            <select
              className="prompt-input"
              value={categoryId}
              disabled={busy}
              onChange={(e) => setCategoryId(e.target.value)}
            >
              <option value="">{t('pt_root')}</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{categoryLabel(c)}</option>
              ))}
            </select>
          </label>
          {canCreateHere ? (
            <label className="publish-tpl-check">
              <input
                type="checkbox"
                checked={creatingCategory}
                disabled={busy}
                onChange={(e) => setCreatingCategory(e.target.checked)}
              />
              {t('pt_new_category_toggle')}
            </label>
          ) : (
            <p className="prompt-choice-hint publish-tpl-hint">{t('pt_max_depth')}</p>
          )}
          {creating && (
            <label className="prompt-label">
              {t('pt_new_category')}
              <input
                className="prompt-input"
                value={newCategory}
                disabled={busy}
                onChange={(e) => setNewCategory(e.target.value)}
              />
            </label>
          )}

          {error && <p className="prompt-error" role="alert">{error}</p>}
          <p className="visually-hidden" role="status">{busy ? t('pt_publishing') : ''}</p>
          <div className="prompt-acts">
            <button type="button" className="mini ghost" onClick={close} disabled={busy}>{t('cancel')}</button>
            <button type="submit" className="mini ok prompt-confirm" disabled={busy || !ready}>
              {busy && <span className="brand-spinner" aria-hidden="true" />}
              {t('pt_publish')}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  )
}

/**
 * The catalogue as a tree of radios: each category (then its sub-categories) with its templates,
 * the root templates last — the catalogue's own order. Only templates of the artefact's type can be
 * picked; the others stay listed, disabled, so the library reads the same as in its explorer.
 */
function TemplatePicker({
  name,
  catalogue,
  loading,
  type,
  selected,
  disabled,
  onPick,
  categoryLabel,
}: {
  name: string
  catalogue: TemplateCatalogue | undefined
  loading: boolean
  type: ArtefactType
  selected: string | null
  disabled: boolean
  onPick: (tpl: TemplateEntry) => void
  categoryLabel: (c: TemplateCategory) => string
}) {
  const { t } = useLang()
  const templates = catalogue?.templates ?? []
  const categories = catalogue?.categories ?? []
  // A template whose category the catalogue does not list reads as a root one — never invisible.
  const atRoot = templates.filter((tpl) => tpl.categoryId === null || !categories.some((c) => c.id === tpl.categoryId))

  const option = (tpl: TemplateEntry) => {
    const other = tpl.type !== type
    return (
      <li key={tpl.id}>
        <label className={other ? 'publish-tpl-other' : undefined}>
          <input
            type="radio"
            name={name}
            value={tpl.id}
            checked={selected === tpl.id}
            disabled={other}
            onChange={() => onPick(tpl)}
          />
          {tpl.name} <span className="publish-tpl-ver">v{tpl.version}</span>
          {other && (
            <span className="publish-tpl-type"> ({t(TYPE_LABEL[tpl.type])} — {t('pt_pick_other_type')})</span>
          )}
        </label>
      </li>
    )
  }

  return (
    <fieldset className="prompt-choice publish-tpl-picker" disabled={disabled}>
      <legend>{t('pt_pick')}</legend>
      {loading ? (
        <p className="prompt-choice-hint">{t('pt_pick_loading')}</p>
      ) : templates.length === 0 ? (
        <p className="prompt-choice-hint">{t('pt_pick_empty')}</p>
      ) : (
        <>
          {categories.map((c) => {
            const inIt = templates.filter((tpl) => tpl.categoryId === c.id)
            if (inIt.length === 0) return null
            return (
              <div key={c.id} className="publish-tpl-cat">
                <p className="publish-tpl-cat-title" aria-hidden="true">{categoryLabel(c)}</p>
                <div role="group" aria-label={categoryLabel(c)}><ul>{inIt.map(option)}</ul></div>
              </div>
            )
          })}
          {atRoot.length > 0 && (
            <div className="publish-tpl-cat">
              <p className="publish-tpl-cat-title" aria-hidden="true">{t('pt_root')}</p>
              <div role="group" aria-label={t('pt_root')}>
                <ul>{atRoot.map(option)}</ul>
              </div>
            </div>
          )}
        </>
      )}
    </fieldset>
  )
}
